import React, { useState, useEffect, lazy, Suspense, useCallback } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { ToastContainer as ReactToastifyContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
const DemoPlayer = lazy(() => import("@/demo/DemoPlayer"));
const RegistrationFormDemoFiller = lazy(() => import("@/demo/RegistrationFormDemoFiller"));
import { DevInfoPanel } from "@/components/Dev/DevInfoPanel";
import { HttpsRedirect } from "@/components/Security/HttpsRedirect";
import "@/utils/SessionPersistenceTest";
import DevFormAutofillButton from "@/components/Dev/DevFormAutofillButton";
import { useInactivityTimeout } from "@/hooks/useInactivityTimeout";
import EmailNotVerifiedListener from "@/components/Security/EmailNotVerifiedListener";

import PrivateRoute from "./components/PrivateRoute";
import { GlobalProvider, useGlobal } from "./context/GlobalContext";
import { ToastProvider } from "@/context/ToastContext";
import { appRoutes } from "./routes";
import PreviewSitemap from "@/components/PreviewSitemap";
import Loading from "@/components/Shared/Loading";
import { isDevToolsEnabled, isProductionEnvironment } from "@/utils/devTools";
   
// --- Original DevTaskBar restored ---
const DevTaskBar: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="fixed right-0 top-1/2 -translate-y-1/2 bg-purple-600 hover:bg-purple-700 text-white px-2 py-4 rounded-l-lg shadow-lg transition-all z-[9998] text-xs font-medium"
                title="Toggle Developer Tools"
            >
                {isOpen ? '→' : '← DEV'}
            </button>
            <div
                className={`fixed right-0 top-0 h-full bg-slate-900/95 backdrop-blur-md border-l border-slate-700 shadow-2xl transition-transform duration-300 z-[9999] overflow-y-auto ${
                    isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
                style={{ width: '400px', maxWidth: '90vw' }}
            >
                <div className="bg-slate-900 border-slate-700 sticky top-0 z-10 flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-white text-lg font-bold">Developer Tools</h2>
                    <button
                        onClick={() => setIsOpen(false)}
                        className="text-slate-400 hover:text-white transition"
                        title="Close"
                    >
                        ✕
                    </button>
                </div>
                {isOpen && <DevInfoPanel />}
            </div>
        </>
    );
};

const PreviewTopBar: React.FC = () => {
    const { PreviewMode } = useGlobal();
    if (!PreviewMode) return null;
    return <div className="z-[99999] absolute left-0 top-0 h-[32px] w-full" />;
};

const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { PreviewMode, getIsAuthenticated, logout } = useGlobal();
    const navigate = useNavigate();

    const handleInactivityTimeout = useCallback(async () => {
        await logout();
        navigate('/', { replace: true });
    }, [logout, navigate]);

    useInactivityTimeout(handleInactivityTimeout, getIsAuthenticated());

    return (
        <div className="relative overflow-hidden" style={{ zIndex: 1 }}>
            {PreviewMode && <PreviewSitemap />}
            <div style={{ marginLeft: PreviewMode ? 256 : 0 }}>
                {children}
            </div>
        </div>
    );
};

const App: React.FC = () => {
    const [isDemo, setIsDemo] = useState(false);
    const [isDevLikeEnvironment, setIsDevLikeEnvironment] = useState(false);
    const [appRunOnce, setAppRunOnce] = useState(false);

    useEffect(() => {
        if (!appRunOnce) {
            setAppRunOnce(true);
            setIsDemo(new URLSearchParams(window.location.search).get("demo") === "true");
            setIsDevLikeEnvironment(isDevToolsEnabled());
            document.querySelectorAll<HTMLInputElement>("input[required][id]").forEach(input => {
                const label = document.querySelector<HTMLLabelElement>(`label[for="${input.id}"]`);
                if (label && !label.querySelector(".required-asterisk")) {
                    const star = document.createElement("span");
                    star.textContent = " *";
                    star.className = "required-asterisk";
                    label.appendChild(star);
                }
            });
        }
    }, [appRunOnce]);

    return (
        <>
            <GlobalProvider>
                {/* forceInProduction previously only listed "production"/"staging" by name, so motodev
                    (and every other deployed mode) served plain HTTP without ever redirecting to
                    HTTPS. Now keys off the same single source of truth as the dev-tools gates. */}
                <HttpsRedirect enabled={true} excludeLocalhost={false} forceInProduction={isProductionEnvironment()} />
                <div className="fixed inset-0" style={{ height: '100vh', width: '100%', zIndex: -50, background: "#050505" }} />
                <AppLayout>
                    <ToastProvider>
                        <EmailNotVerifiedListener />
                        <PreviewTopBar />
                        <Suspense fallback={<Loading message="Loading page..." fullscreen minDurationMs={300} />}> 
                            <Routes>
                                {appRoutes.map(({ path, element, protected: isProtected }) => isProtected ? (
                                    <Route key={path} element={<PrivateRoute />}>
                                        <Route path={path} element={element} />
                                    </Route>
                                ) : (
                                    <Route key={path} path={path} element={element} />
                                ))}
                            </Routes>
                        </Suspense>
                        {isDemo && (<><DemoPlayer /><RegistrationFormDemoFiller /></>)}
                        {isDevLikeEnvironment && <DevTaskBar />}
                        {isDevLikeEnvironment && <DevFormAutofillButton />}
                        <ReactToastifyContainer position="top-right" />
                    </ToastProvider>
                </AppLayout>
            </GlobalProvider>
        </>
    );
};

export default App;
